import { FC } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import styles from './multiSentimentSplit.module.scss'
import { MultiCampaign, MultiSentimentRow } from '../interfaces'
import { campaignColor } from '../palette'
import { wrapLabel } from '../wrapLabel'

interface MultiSentimentSplitProps {
  campaigns: MultiCampaign[]
  insight: string[]
  rows: MultiSentimentRow[]
}

const COPY = {
  campaign: 'Campaign',
  subtitle: 'Share of positive, neutral and negative mentions.',
  title: 'Sentiment Split',
}

// Bottom to top, the way the bars stack.
const BANDS = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#c7ced1', key: 'neutral', label: 'Neutral' },
  { color: '#ff6833', key: 'negative', label: 'Negative' },
] as const

type BandKey = (typeof BANDS)[number]['key']

const CHART_HEIGHT = 330
const CHART_WIDTH = 1130

// A band shorter than this cannot hold two lines of text without them landing
// on the band above.
const LABEL_FITS = 34

const total = (row: MultiSentimentRow) =>
  row.positive + row.neutral + row.negative

const share = (row: MultiSentimentRow, key: BandKey) => {
  const sum = total(row)

  return sum ? Math.round((row[key] / sum) * 100) : 0
}

const formatCount = (value: number) => value.toLocaleString('en-US')

// Four stacks against one axis, then the same numbers as a table.
//
// The axis counts mentions, as the design draws it and as QA asked: a stack's
// height is what it collected, so the columns are comparable to each other and
// not only to themselves. Each band still states its share above its count.
//
// A campaign with many times its neighbours' mentions does press them toward
// the baseline. That is the honest picture on a shared axis, and the table
// underneath carries every figure the bands are then too short to print.

// A round number at or above the tallest stack, so the ticks read 0, 800,
// 1600 … rather than 0, 550.2. The right-hand axis carries no series of its
// own, so without an explicit range ECharts has nothing to scale it by and
// draws it blank.
const AXIS_STEPS = 5

const axisMax = (tallest: number) => {
  if (tallest <= 0) return AXIS_STEPS

  const rough = tallest / AXIS_STEPS
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const step = [1, 1.5, 2, 2.5, 3, 5, 10].find(
    (nice) => nice * magnitude >= rough
  )

  return (step ?? 10) * magnitude * AXIS_STEPS
}
export const MultiSentimentSplit: FC<MultiSentimentSplitProps> = ({
  campaigns,
  insight,
  rows,
}) => {
  // Ordered by the campaigns, not by the rows: a row missing for a campaign
  // would otherwise shift every stack after it under the wrong name.
  const ordered = campaigns.map((campaign) => ({
    campaign,
    row: rows.find((item) => item.campaignId === campaign.id),
  }))

  const tallest = Math.max(
    ...ordered.map(({ row }) => (row ? total(row) : 0)),
    0
  )
  const top = axisMax(tallest)

  // The same axis twice, so a band is the same height whichever column it is
  // in and the reader can measure from either flank. Both carry the rule the
  // design draws down each side of the plot.
  const valueAxis = (showSplitLine: boolean): EChartOption.YAxis => ({
    axisLabel: {
      color: '#000000b2',
      fontFamily: 'Roboto, sans-serif',
      fontSize: 12,
      formatter: (value: number) => formatCount(value),
    },
    axisLine: { lineStyle: { color: '#e6ebfa' }, show: true },
    axisTick: { show: false },
    interval: top / AXIS_STEPS,
    max: top,
    min: 0,
    splitLine: {
      lineStyle: { color: '#e6ebfa', type: 'dashed' as const },
      show: showSplitLine,
    },
    type: 'value',
  })

  const options: EChartOption = {
    // Room on both flanks for the two value axes; at 8 the right-hand one had
    // nowhere to draw its labels and was dropped silently. `containLabel` puts
    // the axis labels INSIDE the grid, so anything left at the bottom is empty
    // canvas — at 56 it left a band of white above the table.
    grid: { bottom: 8, containLabel: true, left: 16, right: 16, top: 16 },
    series: BANDS.map((band) => ({
      barWidth: 92,
      data: ordered.map(({ row }) => (row ? row[band.key] : 0)),
      itemStyle: {
        color: band.color,
        // Only the top band is rounded, so the stack reads as one column with
        // a cap rather than three stacked pills.
        ...(band.key === 'negative' ? { barBorderRadius: [8, 8, 0, 0] } : {}),
      },
      label: {
        // The share above the count, as the mock reads it. Hidden on a band too
        // short to hold them.
        formatter: (params: { data: number; dataIndex: number }) => {
          const row = ordered[params.dataIndex]?.row

          if (!row) return ''

          // The band's own height in pixels: too short and its two lines would
          // print across the band below and over the campaign's name.
          const height = top ? (params.data / top) * (CHART_HEIGHT - 72) : 0

          return height < LABEL_FITS
            ? ''
            : `{share|${share(row, band.key)}%}\n{value|${formatCount(
                params.data
              )}}`
        },
        position: 'inside',
        rich: {
          share: {
            color: '#25262d',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 16,
          },
          value: {
            color: '#25262d',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 11,
            fontWeight: 400,
            lineHeight: 14,
          },
        },
        show: true,
      },
      name: band.label,
      stack: 'sentiment',
      type: 'bar',
    })),
    tooltip: {
      axisPointer: { type: 'none' },
      // Counts on the bars, so the tooltip carries the share beside them.
      formatter: (params) => {
        const rows = Array.isArray(params) ? params : [params]
        const row = ordered[rows[0]?.dataIndex ?? -1]?.row

        return [
          rows[0]?.name ?? '',
          ...rows.map((entry) => {
            const band = BANDS.find((item) => item.label === entry.seriesName)
            const pct = row && band ? share(row, band.key) : 0

            return `${entry.seriesName}: ${formatCount(
              Number(entry.value)
            )} (${pct}%)`
          }),
        ].join('<br/>')
      },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        // A campaign name is longer than its column, so it is broken onto two
        // lines here rather than dropped whole by ECharts.
        formatter: (name: string) => wrapLabel(name, 34),
        margin: 16,
      },
      axisLine: { lineStyle: { color: '#e6ebfa' } },
      axisTick: { show: false },
      data: ordered.map(({ campaign }) => campaign.name),
      type: 'category',
    },
    // Both sides, as the mock draws it: with four stacks the right-hand column
    // is a long way from the left axis.
    yAxis: [valueAxis(true), valueAxis(false)],
  }

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{COPY.subtitle}</p>
        </div>

        <ul className={styles.legend}>
          {BANDS.map((band) => (
            <li className={styles.legendItem} key={band.key}>
              <span
                className={styles.legendDot}
                style={{ background: band.color }}
              />
              {band.label}
            </li>
          ))}
        </ul>
      </div>

      <EChart height={CHART_HEIGHT} options={options} width={CHART_WIDTH} />

      <div className={styles.table}>
        <div className={styles.tableHead}>
          <span>{COPY.campaign}</span>
          {BANDS.map((band) => (
            <span key={band.key}>{band.label}</span>
          ))}
        </div>

        {ordered.map(({ campaign, row }, index) => (
          <div className={styles.tableRow} key={campaign.id}>
            <span className={styles.tableCampaign}>
              <span
                className={styles.tableDot}
                style={{ background: campaignColor(index) }}
              />
              <TruncatedTooltip
                className={styles.tableName}
                maxLines={2}
                text={campaign.name}
              />
            </span>

            {BANDS.map((band) => (
              <span className={styles.tableValue} key={band.key}>
                {row
                  ? `${formatCount(row[band.key])} (${share(row, band.key)}%)`
                  : '—'}
              </span>
            ))}
          </div>
        ))}
      </div>

      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />

        <div className={styles.insightText}>
          {insight.map((paragraph) => (
            <p className={styles.insightParagraph} key={paragraph}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  )
}
